import type { OrderDTO, PrintingStatus, ServerWsMessage, SyncEvent } from "@bb/protocol";
import type { Api } from "./api.ts";

export type ConnectionState = "connecting" | "online" | "offline";

/**
 * Utrzymuje lokalną kopię zamówień lokalu zgodną z serwerem.
 * - WebSocket z automatycznym wznawianiem (backoff + losowy rozrzut) i heartbeatem;
 * - każde zdarzenie ma numer seq; luka w numeracji = dociągnięcie brakujących przez /sync;
 * - po każdym ponownym połączeniu (np. Wi-Fi → 5G) porównujemy seq z serwerem.
 */
export class SyncEngine {
  orders = new Map<string, OrderDTO>();
  seq = 0;
  state: ConnectionState = "connecting";
  lastEventAt: number | null = null;
  /** Stan drukowania lokalu (bramka / kasa drukująca); null = jeszcze nieznany. */
  printing: PrintingStatus | null = null;
  /** Wołane, gdy administrator zmienił kartę. */
  onCatalogChanged: (() => void) | null = null;

  private ws: WebSocket | null = null;
  private backoff = 500;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private lastPong = 0;
  private catchingUp: Promise<void> | null = null;
  private stopped = false;
  private listeners = new Set<() => void>();

  constructor(
    private readonly api: Api,
    private readonly deviceToken: () => string | null,
  ) {}

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  async start(): Promise<void> {
    this.stopped = false;
    if (typeof window !== "undefined") {
      window.addEventListener("online", this.onBrowserOnline);
      window.addEventListener("offline", this.onBrowserOffline);
    }
    await this.fullReload().catch(() => this.setState("offline"));
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.onBrowserOnline);
      window.removeEventListener("offline", this.onBrowserOffline);
    }
    this.clearTimers();
    this.ws?.close();
    this.ws = null;
  }

  /** Wymusza sprawdzenie zaległości (np. po przyjęciu komendy, gdy WebSocket jeszcze nie wrócił). */
  catchUp(): Promise<void> {
    this.catchingUp ??= this.doCatchUp().finally(() => (this.catchingUp = null));
    return this.catchingUp;
  }

  private async fullReload(): Promise<void> {
    const { seq, orders } = await this.api.get<{ seq: number; orders: OrderDTO[] }>("/api/v1/orders");
    this.orders = new Map(orders.map((o) => [o.id, o]));
    this.seq = seq;
    this.emit();
    await this.catchUp();
  }

  private async doCatchUp(): Promise<void> {
    for (;;) {
      const { events, hasMore } = await this.api.get<{ events: SyncEvent[]; hasMore: boolean }>(`/api/v1/sync?since=${this.seq}`);
      for (const e of events) this.apply(e);
      if (!hasMore) break;
    }
  }

  private apply(e: SyncEvent): void {
    if (e.seq <= this.seq) return; // już mamy
    if (e.seq > this.seq + 1) {
      void this.catchUp(); // luka — dociągamy po kolei
      return;
    }
    this.seq = e.seq;
    this.lastEventAt = Date.now();
    if (e.order) {
      const cur = this.orders.get(e.order.id);
      if (!cur || cur.version <= e.order.version) this.orders.set(e.order.id, e.order);
    }
    this.emit();
  }

  private connect(): void {
    if (this.stopped) return;
    const token = this.deviceToken();
    if (!token) return;
    this.setState("connecting");
    const ws = new WebSocket(this.api.wsUrl(`/api/v1/ws?token=${encodeURIComponent(token)}`));
    this.ws = ws;
    ws.onopen = () => {
      this.lastPong = Date.now();
      ws.send(JSON.stringify({ t: "hello", lastSeq: this.seq }));
      this.pingTimer = setInterval(() => {
        // Brak odpowiedzi przez 25 s = połączenie martwe (np. tablet zmienił sieć) → zrywamy i łączymy od nowa.
        if (Date.now() - this.lastPong > 25_000) return ws.close();
        ws.send(JSON.stringify({ t: "ping", at: Date.now() }));
      }, 10_000);
    };
    ws.onmessage = (m) => {
      this.lastPong = Date.now();
      const msg = JSON.parse(String(m.data)) as ServerWsMessage;
      if (msg.t === "welcome") {
        this.backoff = 500;
        this.printing = msg.printing;
        this.setState("online");
        this.emit();
        if (msg.seq > this.seq) void this.catchUp().catch(() => ws.close());
      } else if (msg.t === "event") {
        this.apply(msg.event);
      } else if (msg.t === "catalog") {
        this.onCatalogChanged?.();
      } else if (msg.t === "printing") {
        this.printing = msg.printing;
        this.emit();
      } else if (msg.t === "resync") {
        void this.fullReload().catch(() => ws.close());
      }
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.clearTimers();
      this.ws = null;
      this.setState("offline");
      this.scheduleReconnect();
    };
    ws.onerror = () => ws.close();
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer) return;
    const delay = this.backoff * (0.75 + Math.random() * 0.5);
    this.backoff = Math.min(this.backoff * 2, 10_000);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private onBrowserOnline = () => {
    this.backoff = 500;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    if (!this.ws) this.connect();
  };

  private onBrowserOffline = () => this.ws?.close();

  private clearTimers(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private setState(s: ConnectionState): void {
    if (this.state === s) return;
    this.state = s;
    this.emit();
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }
}
